import {
  Box,
  Button,
  Divider,
  Flex,
  List,
  ListIcon,
  ListItem,
  Text,
  Tooltip,
} from '@chakra-ui/react'
import Trans from 'next-translate/Trans'
import useTranslation from 'next-translate/useTranslation'

import { Card } from 'components/common/Card'
import { Tag } from 'components/common/Tag'
import { CheckIcon } from 'components/icons/CheckIcon'
import { SubscriptionPlanType } from 'constants/subscription'
import { SubscriptionTier } from 'generated/generated-graphql'

import { SubscriptionPrice } from './SubscriptionPrice'

export interface SubscriptionCardProps {
  title: string
  tier: SubscriptionTier
  price: number
  isYearlyPeriod?: boolean
  caption?: string
  buttonTooltipText?: string
  isDisabled?: boolean
  bulletPoints?: string[]
  currentPlan?: SubscriptionPlanType
  onSubscriptionPlanClick?: (tier: SubscriptionTier) => void
}

export const SubscriptionCard = ({
  title,
  tier,
  price,
  buttonTooltipText,
  isDisabled,
  currentPlan,
  bulletPoints = [],
  isYearlyPeriod,
  onSubscriptionPlanClick,
}: SubscriptionCardProps) => {
  const { t } = useTranslation('common')

  const isDowngrade =
    (isYearlyPeriod && currentPlan?.yearlyPrice
      ? currentPlan.yearlyPrice
      : (currentPlan?.price ?? 0)) >= price

  return (
    <Card
      w={{ base: 'full', xl: '400px' }}
      variant="subscription"
      p="32px"
      contentBoxProps={{ m: 0, h: 'full' }}
    >
      <Flex direction="column" alignItems="start" position="relative" h="full">
        <Flex justifyContent="space-between" w="full">
          <Text
            color="brand.purpleNavy"
            fontSize="24px"
            lineHeight="24px"
            fontFamily="Proxima Nova"
          >
            {title}
          </Text>
          {currentPlan?.subscriptionTier === tier && (
            <Tag
              gap="8px"
              px="12px"
              py="8px"
              label={t('Subscriptions.currentPlanTag')}
              leftIcon={
                <CheckIcon color="brand.secondary.orange700" mt="-3px" />
              }
              color="brand.secondary.orange700"
              bgColor="brand.secondary.orange100"
            />
          )}
        </Flex>

        {price && (
          <SubscriptionPrice price={price} isYearlyPeriod={isYearlyPeriod} />
        )}

        <List mt="8px" spacing="12px" textAlign="left">
          {bulletPoints.map((bulletPoint) => (
            <ListItem
              key={bulletPoint}
              w="full"
              fontSize="16px"
              lineHeight="20px"
            >
              <ListIcon
                as={CheckIcon}
                color="brand.ultramarine"
                w="20px"
                h="20px"
              />
              <Trans
                i18nKey={`common:${bulletPoint}`}
                components={{
                  bold: <b />,
                }}
              />
            </ListItem>
          ))}
        </List>

        <Box marginTop="auto" w="full">
          <Divider mt="16px" mb="16px" w="full" />

          <Tooltip label={buttonTooltipText}>
            <Box w="full">
              <Button
                isDisabled={isDisabled || isDowngrade}
                variant="outline"
                w="full"
                h="40px"
                onClick={() => {
                  onSubscriptionPlanClick?.(tier)
                }}
              >
                {currentPlan
                  ? isDowngrade
                    ? t('VENUE_PAGE_TEXT32')
                    : t('Subscriptions.upgrade')
                  : t('VENUE_PAGE_TEXT32')}
              </Button>
            </Box>
          </Tooltip>
        </Box>
      </Flex>
    </Card>
  )
}
